<template>
  <div>
    <div class="container">
      <div v-if="currentStrategy.isMultipleWindows">
        <PricingStrategyNameDaysComponent
          :name="windowProp.name"
          :description="windowProp.description"
          :daysIn="windowProp.daysIn"
          :daysOut="windowProp.daysOut"
          :invalidDaysIn="invalidDaysIn(windowProp.id)"
          :invalidDaysOut="invalidDaysOut(windowProp.id)"
          :daysInMoreThanDayOut="daysInMoreThanDayOut(windowProp.id)"
          :invalidName="invalidName(windowProp.id)"
          @updateWindow="handleUpdateWindowProp"
        />
      </div>

      <!-- Display Price Section -->
      <div class="row mb-4">
        <div v-if="currentStrategy.isAdjustByUtilization">
          <!-- Adjust the price as the tee sheet fills section -->
          <UtilizationPricingSettings
            :ref="(el) => { $emit('update:dailyUtilizationSettingsRef', el as ComponentPublicInstance | null) }"
            :initial-data="utilizationPricesByWindowId[windowProp.id ?? 0]"
            :is-all-week="true"
            :is-all-day="true"
            :price-window-id="windowProp.id ?? 0"
            @save-success="handlePricingSaveSuccess"
            @save-error="handlePricingSaveError"
          />
        </div>
        <div class="col-md-12" v-if="!currentStrategy.isAdjustByUtilization">
          <div
            class="p-3 d-flex justify-content-end align-items-center price-display"
          >
            <span>Display at</span>
            <PriceUnitComponent
              :value="getUtilizationPrice(windowProp.id ?? 0, 0)"
              :isPercent="getUsePercent(windowProp.id ?? 0, 0)"
              @update:value="
                updateUtilizationPrice(windowProp.id ?? 0, 0, $event)
              "
              @update:isPercent="
                updateUsePercent(windowProp.id ?? 0, 0, $event)
              "
            ></PriceUnitComponent>
            <i
              class="bi bi-info-circle"
              v-tooltip="{
                content:
                  'When applying this strategy:<br>If % is selected, a booking channel\'s pricing will be shown as this percentage of the regular rate.<br>If $ is selected, this amount will be shown.',
                placement: 'top',
                triggers: ['hover', 'focus'],
                html: true,
                popperClass: 'custom-tooltip',
              }"
              tabindex="0"
              role="button"
              aria-label="Info"
            ></i>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Strategy, UtilizationPrice } from '@/api';
import { ComponentPublicInstance, defineComponent, PropType } from 'vue';

import PriceUnitComponent from '../PriceUnitComponent.vue';
import PricingStrategyNameDaysComponent from './PricingStrategyNameDaysComponent.vue';
import UtilizationPricingSettings from '../UtilizationPricingSettings.vue';

export default defineComponent({
  name: 'PricingAllDaysStrategyComponent',
  components: {
    PriceUnitComponent,
    PricingStrategyNameDaysComponent,
    UtilizationPricingSettings,
  },
  emits: ['updateWindow', 'update:dailyUtilizationSettingsRef'],
  props: {
    windowProp: {
      type: Object,
      required: true,
    },
    currentStrategy: {
      type: Object as PropType<Strategy>,
      required: true,
    },
    utilizationPricesByWindowId: {
      type: Object as PropType<Record<number, UtilizationPrice[]>>,
      required: true,
    },
    dailyUtilizationSettingsRef: {
      type: Object as PropType<ComponentPublicInstance | undefined | null>,
      required: false,
    },
    getUtilizationPrice: {
      type: Function,
      required: true,
    },
    getUsePercent: {
      type: Function,
      required: true,
    },
    updateUtilizationPrice: {
      type: Function,
      required: true,
    },
    updateUsePercent: {
      type: Function,
      required: true,
    },
    handlePricingSaveSuccess: {
      type: Function as PropType<() => void>,
      required: true,
    },
    handlePricingSaveError: {
      type: Function as PropType<(error: string) => void>,
      required: true,
    },
    invalidDaysIn: {
      type: Function,
      required: true,
    },
    invalidDaysOut: {
      type: Function,
      required: true,
    },
    daysInMoreThanDayOut: {
      type: Function,
      required: true,
    },
    invalidName: {
      type: Function,
      required: true,
    },
  },
  setup(props, { emit }) {
    const handleUpdateWindowProp = (
      key: 'name' | 'description' | 'daysIn' | 'daysOut',
      value: string | number
    ) => {
      emit('updateWindow', props.windowProp, key, value);
    };

    return {
      handleUpdateWindowProp,
    };
  },
});
</script>

<style scoped>
/* Add these new styles for consistency */
.form-control:focus,
.form-select:focus {
  border-color: #618183;
  box-shadow: 0 0 0 0.25rem rgba(64, 95, 107, 0.25);
}

.price-display {
  display: flex;
  align-items: center;
  background-color: #98b2b4;
  color: white;
  margin-bottom: 0px !important;
}

.bi-info-circle {
  margin-left: 5px;
}

.daysRange-input {
  width: 90px;
  margin-left: 5px;
}
</style>
